import { css, cx, keyframes } from '@emotion/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { make as RichText } from '@prezzee/ribbon/src/components/personal/RichText/RichText.gen';
import { useEffect, useRef, useState } from 'react';

interface Props {
  heading?: string;
  text: string;
  isVisible: boolean;
  dismissHandler?: () => void;
  timeout?: number;
}

const fadeOut = keyframes`
  0% {
    visibility: visible;
    opacity: 1;
    display: flex;
  }
  100% {
    visibility: hidden;
    opacity: 0;
    display: none;
  }
`;

const style = {
  container: css`
    position: fixed;
    z-index: 3;
    background-color: #fcefe6;
    padding: 16px 20px;
    border-radius: 10px;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.07);
    visibility: visible;
    display: flex;
    opacity: 1;

    @media (max-width: 767px) {
      margin-left: 1rem;
      margin-right: 1rem;
      width: calc(100% - 2rem);
    }

    @media (min-width: 768px) {
      right: 24px;
      width: calc(100% - 48px);
      max-width: 560px;
    }
  `,
  containerHidden: css`
    animation: ${fadeOut} 0.3s linear;
    animation-fill-mode: forwards;
  `,
  containerOffset: (offset: number) => {
    return css`
      top: ${offset + 18}px;
    `;
  },
  icon: css`
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    line-height: 21px;
    border-radius: 50%;
    margin-right: 16px;
    background-color: #dc5800;
    color: #ffffff;
    text-align: center;
    font-weight: 700;
    margin-top: 4px;
  `,
  header: css`
    display: flex;
  `,
  heading: css`
    font-size: 20px;
    line-height: 28px;
    margin: 0;
    flex: 1 1 auto;
  `,
  btnDismiss: css`
    flex: 0 0 0%;
    font-size: 14px;
    line-height: 20px;
    padding-left: 5px;
    padding-right: 5px;
    transform: translateX(5px);

    &:focus {
      outline: none;
    }
  `,
  text: css`
    flex: 1 1 auto;
    max-width: calc(100% - 40px);
    color: #444444;
    font-size: 1rem;
    line-height: 1.5;

    p {
      margin: 4px 0 0;
    }
  `,
};

const ProductDescriptionAlert = ({
  heading = '',
  text,
  isVisible = false,
  dismissHandler = () => {},
  timeout = 0,
}: Props) => {
  const dismissTimer = useRef(setTimeout(() => null, 0));
  const container = useRef(null);
  const [offset, updateOffset] = useState(0);

  const updatePopupPlacement = () => {
    const mainArr = document.getElementsByTagName('main');
    if (mainArr.length) {
      const main = mainArr[0];
      const previousElementSibling = main.previousElementSibling;
      if (previousElementSibling) {
        updateOffset(previousElementSibling.clientHeight);
      }
    }
  };

  useEffect(() => {
    if (timeout && dismissHandler) {
      dismissTimer.current = setTimeout(dismissHandler, timeout);
    }

    return () => {
      clearTimeout(dismissTimer.current);
    };
  }, []);

  useEffect(() => {
    if (container?.current && typeof window !== 'undefined') {
      updatePopupPlacement();
      window.addEventListener('resize', updatePopupPlacement);
    }
  }, [container]);

  return (
    <div
      className={cx([style.container, isVisible ? '' : style.containerHidden, style.containerOffset(offset)])}
      ref={container}
    >
      <span className={style.icon}>!</span>
      <div className={style.text}>
        <div className={style.header}>
          <h3 className={style.heading}>{heading}</h3>
          <button type="button" className={style.btnDismiss} onClick={dismissHandler}>
            <FontAwesomeIcon icon={['far', 'times']} />
          </button>
        </div>
        {text ? <RichText>{text}</RichText> : null}
      </div>
    </div>
  );
};

export default ProductDescriptionAlert;
